import { InmateHistory, InmateHistoryJson } from '@reliance/types/models/InmateHistory';
import { Inmate } from '@reliance/types/models/Inmate';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo } from 'react';
import { useInmateHistoryByInmateId } from '../../hooks/InmateHistory';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { formatDate } from '../../format/DateTime';

type InmateChangeHistoryProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

// Pretty format the change value for display
function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (typeof value === 'object' && value !== null && 'id' in value && typeof (value as { id: unknown }).id === 'number') {
    return `ID #${(value as { id: number }).id}`;
  }
  if (typeof value === 'object' && value !== null) {
    return 'set';
  }
  return String(value);
}

function formatJsonChanges(json: InmateHistoryJson): string {
  if (!json || !json.changes || typeof json.changes !== 'object') {
    return '';
  }
  const changeKeys = Object.keys(json.changes);
  return changeKeys.reduce((acc, key) => {
    const val = json.changes![key];
    const part = Array.isArray(val)
      ? `${key}: ${formatChangeValue(val[0])} → ${formatChangeValue(val[1])}`
      : `${key}=${formatChangeValue(val)}`;
    return acc.length !== 0 ? `${acc}, ${part}` : part;
  }, '');
}

function hadChange(row: InmateHistory, field: string): boolean {
  return Boolean(row.json?.changes && Object.prototype.hasOwnProperty.call(row.json.changes, field));
}

const CHANGED_CELL_CLASS = 'bg-warning bg-opacity-25 rounded px-2 py-1 d-block w-100';
const CHANGED_CELL_SUCCESS_CLASS = 'bg-success bg-opacity-25 rounded px-2 py-1 d-block w-100';
const CHANGED_CELL_DANGER_CLASS = 'bg-danger bg-opacity-25 rounded px-2 py-1 d-block w-100';

function CellWithHighlight({
  children,
  highlight,
  tone
}: {
  children: React.ReactNode;
  highlight: boolean;
  tone?: 'success' | 'danger' | 'default';
}) {
  if (!highlight) {
    return <>{children}</>;
  }

  let className = CHANGED_CELL_CLASS;
  if (tone === 'success') {
    className = CHANGED_CELL_SUCCESS_CLASS;
  } else if (tone === 'danger') {
    className = CHANGED_CELL_DANGER_CLASS;
  }

  return <span className={className}>{children}</span>;
}

const InmateChangeHistory: React.FC<InmateChangeHistoryProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;

  const historyResult = useInmateHistoryByInmateId(null) as {
    result?: InmateHistory[];
    loading: boolean;
    execute: (id: number) => void;
  };

  useNotifyTabLoading('changes', historyResult.loading);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        historyResult.execute(inmate.id);
      }
    }, [historyResult, inmate.id]),
  });

  const data = historyResult.result ?? [];

  const columnHelper = createColumnHelper<InmateHistory>();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.dateChanged, {
        id: 'dateChanged',
        header: 'Date',
        cell: (info) => formatDate(info.getValue()),
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.json?.newInmate?.status ?? row.status, {
        id: 'status',
        header: 'General Status',
        cell: (info) => {
          const value = info.getValue() ?? '';
          const tone = value === 'Enabled' ? 'success' : value === 'Disabled' ? 'danger' : 'default';
          return (
            <CellWithHighlight highlight={hadChange(info.row.original, 'status')} tone={tone}>
              {value}
            </CellWithHighlight>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.json?.newInmate?.phoneStatus ?? row.inmate?.phoneStatus, {
        id: 'phoneStatus',
        header: 'Audio Calling',
        cell: (info) => {
          const value = info.getValue() ?? '';
          const tone = value === 'Enabled' ? 'success' : value === 'Disabled' ? 'danger' : 'default';
          return (
            <CellWithHighlight highlight={hadChange(info.row.original, 'phoneStatus')} tone={tone}>
              {value}
            </CellWithHighlight>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.json?.newInmate?.audioStatus ?? row.inmate?.audioStatus, {
        id: 'audioStatus',
        header: 'Wireless Audio Calling',
        cell: (info) => {
          const value = info.getValue() ?? '';
          const tone = value === 'Enabled' ? 'success' : value === 'Disabled' ? 'danger' : 'default';
          return (
            <CellWithHighlight highlight={hadChange(info.row.original, 'audioStatus')} tone={tone}>
              {value}
            </CellWithHighlight>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.json?.newInmate?.textStatus ?? row.inmate?.textStatus, {
        id: 'textStatus',
        header: 'Texting',
        cell: (info) => {
          const value = info.getValue() ?? '';
          const tone = value === 'Enabled' ? 'success' : value === 'Disabled' ? 'danger' : 'default';
          return (
            <CellWithHighlight highlight={hadChange(info.row.original, 'textStatus')} tone={tone}>
              {value}
            </CellWithHighlight>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.housingUnit?.name, {
        id: 'housingUnit',
        header: 'Housing Unit',
        cell: (info) => {
          const row = info.row.original;
          return (
            <CellWithHighlight highlight={hadChange(row, 'housingUnit') || hadChange(row, 'housingUnitId')}>
              {info.getValue() ?? ''}
            </CellWithHighlight>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.json, {
        id: 'json',
        header: 'All Changes',
        cell: (info) => {
          const row = info.row.original;
          const hasChanges = Boolean(row.json?.changes && Object.keys(row.json.changes).length > 0);
          return (
            <CellWithHighlight highlight={hasChanges}>
              {formatJsonChanges(info.getValue() ?? {})}
            </CellWithHighlight>
          );
        },
        enableColumnFilter: false,
      }),
    ],
    [columnHelper]
  );

  return (
    <div className="jarviswidget">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <BaseHtmlTable
              rows={data}
              columnDefs={columns}
              paginationMode="client"
              perPage={200}
              loading={historyResult.loading}
              pageSizeOptions={[100, 200]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InmateChangeHistory;
